// vendor
import { defineComponent, ref, computed, PropType, watch, SetupContext } from "@vue/composition-api";
import { format as formatDate } from "date-fns";
// project
import { formatDateStringRecent } from "@/utils/text";
import { Asset } from "@/psychlab/types";
import { getAssetTypes } from "@/psychlab/meta";
import { useAssets } from "@/hooks/useAssets";
import { useAssetListDisplay } from "@/hooks/useAssetListDisplay";
import { useTags } from "@/hooks/useTags";
import * as AppModal from "@/AppModal";
import { useUserPrefs } from "@/hooks/useUserPrefs";
// local
import * as Utils from "./utils";

import { useTranslate } from "@lang";
import { AbsBox, Pagination, Divider, Date as DisplayDate } from "@ui";


const AssetTypes:any = {};
getAssetTypes().forEach(t => AssetTypes[t.name] = t);

type DragTransformFn = (item:any) => any;

const Hoverlay = {
	template:`<div
	style="
	position:absolute;
	width:100%;
	height:100%;
	top:0;
	pointer-events:none;
	opacity:0.2;
	"
	class="bg-info"
	>
	</div>`
};

const loadQuerySort = (ctx:SetupContext) => {
	const v = Utils.getQueryValue<string|null>("al_sort", ctx, null);
	if(!v){ return { desc:true, prop:"lastModified" }; }
	const [
		p1,
		p2,
	] = v.split("-");
	
	if(!p2){ return { desc:true, prop:p1 }};
	return { desc: true, prop:p2 };

};

const createAsset = (type:string, context:SetupContext) => {
	const assetHooks = useAssets();
	const tagHooks = useTags();
	AppModal.createAsset({ type }, async (r) => {
		const { name, tags, openEditor } = r;
		const a = await assetHooks.createAsset(type, name);
		await Promise.all(tags.map(t => tagHooks.tagItem(t, a._id, "asset")));
		if(openEditor){
			context.root.$router.push({
				name:"asset.edit",
				params:{
					assetId:a._id,
				}
			})
		}
	});
};

export const AssetList = defineComponent({

	
	props:{
		exclude:{
			type:Array as PropType<string[]>,
			default:() => []
		},
		include:{
			type:Array as PropType<string[]>,
			default:() => []
		},
		dragTransformFn:{
			type:Function as PropType<DragTransformFn>,
			default:() => null
		},
		showAdd:{
			type:Boolean,
			default:true,
		},
		defaultTag:{
			type:String,
		},
		taggingEnabled:{
			type:Boolean,
			default:true
		},
		showContext:{
			type:Boolean,
			default:true
		},
		searchFilter:{
			type:String,
			default:"",
		},
	},
	components:{
		Hoverlay,
		Pagination,
		Divider,
		DisplayDate,
		AbsBox,
	},
	setup(props, context){

		const loading = ref(false);
		const draggedItem = ref<string|null>(null);
		const displayFields = computed(() => tableFields);
		const activeTag = computed(() => props.defaultTag || null);

		const qs = loadQuerySort(context);

		const sortProp = ref(qs.prop);
		const sortDesc = ref(qs.desc);

		const { translate } = useTranslate();

		

		const sortQuery = computed(() => {
			return (sortDesc.value ? "-" : "") + sortProp.value;
		});

		watch([sortProp, sortDesc], () => {
			Utils.setQueryValue("al_sort", sortQuery.value, context);
		});
		
		const onSortChanged = (ctx:{ sortBy:string, sortDesc:boolean }) => {
			sortDesc.value = ctx.sortDesc;
			sortProp.value = ctx.sortBy;
		};

		const {
			hasTag,
			sortedTags
		} = useTags();

		const assetHooks = useAssets();

		
		const {
			pagination,
			openAssetContext,
			getAssetTags,
		} = useAssetListDisplay({
			activeTag,
			context,
			queryPrefix:"al_",
		});


		const {
			assetListLimit
		} = useUserPrefs();

		pagination.limit = assetListLimit.value;

		watch(() => pagination.limit, () => {
			assetListLimit.value = pagination.limit;
		})

		const filterAsset = (a:Asset) => {
			if(props.exclude.includes(a.type)){ return false; }
			if(!props.searchFilter || props.searchFilter.length < 2){ return true; }
			return a.name.toLowerCase().includes(props.searchFilter.toLowerCase());
		};

		const assets = computed(() => {
			const tagged = assetHooks.assets.value.filter(a => (props.defaultTag ? hasTag(props.defaultTag, a._id) : true));
			if(!props.exclude.length){ return tagged }
			return tagged.filter(filterAsset);
		});

		const displayTypes = computed(() => {
			if(props.include.length > 0){
				return props.include;
			}
			return Object.keys(AssetTypes).filter(t => !props.exclude.includes(t));
		});

		const getTypeColor = (t:string) => {
			return AssetTypes[t].color || "white";
		};


		const getAssetTagArgs = (asset:Asset) => {
			return {
				type:"asset",
				item:asset._id
			};
		};

		const openContext = (e:any, asset:Asset) => {
			openAssetContext(e, asset._id);
		};

		const openAsset = (ob:Asset) => {
			context.root.$router.push({ name: "view-asset", params:{ assetId: ob._id } })
		};

		const onDragStart = (e:any, item:Asset) => {
			draggedItem.value = item._id;
			const data = getAssetTagArgs(item);
			e.dataTransfer.setData("tagTarget", data ? JSON.stringify(data) : null);
		};

		const dragEnd = () => {
			draggedItem.value = null;
		};

		const addType = (type:string) => {
			// const typeNameAlias = ({
			// 	"Graph":"Blueprint"
			// } as any);
			
		
			
			// const n = translate(`assets.label.new${typeNameAlias[type] || type}`);
			// AppModal.editText({
			// 	value: n,
			// 	title: n,
			// 	validator(v){
			// 		return v.length > 2;
			// 	}
			// }, async (v:string) => {
			// 	assetHooks.createAsset(type, v, props.defaultTag);
			// });
			createAsset(type, context);
		};

		const canAddType = (t:string) => {
			return !AssetTypes[t].disabled;
		};

		const addAsset = (e:any) => {
			const options:any[] = displayTypes.value
			.filter(canAddType)
			.map(t => {
				return {
					name:translate(`assets.label.${t.toLowerCase()}`),
					icon:getTypeIcon(t),
					fn: ()=> {
						addType(t);
					}
				}
			});
			AppModal.context(e, options);
		};

		const getTypeIcon = (type:string) => AssetTypes[type].icon || "mdi.circle-outline";

		const getDisplayDate = (v:string) => {
			return formatDate(new Date(v), "eeee MMM d y @ HH:mm");
		};

		return {
			assets,
			loading,
			displayFields,
			draggedItem,
			sortProp,
			sortDesc,
			// historyModal,
			pagination,
			openAsset,
			openContext,
			addAsset,
			getTypeIcon,
			onDragStart,
			dragEnd,
			formatDateStringRecent,
			getTypeColor,
			getDisplayDate,
			onSortChanged,
			getAssetTags,
			translate,
		};

	}

});

const tableFields = [
	{
		key:"type",
		sortable:true,
		thClass:"xs-col font-weight-light text-light noselect text-80",
		tdClass:"font-consolas text-center align-middle no-pad"
	},
	
	{
		key:"name",
		sortable:true,
		class:"text-light font-weight-light noselect",
		tdClass:"no-pad",
		thClass:"text-80",
	},
	{
		key:"lastModified",
		label:"Modified",
		sortable:true,
		class:"text-secondarx font-weight-light noselect",
		thClass:"md-col text-light font-weight-light text-80",
		formatter:formatDateStringRecent,
		tdClass:"text-80 text-secondaryx no-pad font-nats opacity-06"
	},
	{
		key:"tags",
		thClass:"md-col text-light font-weight-light text-80",
		tdClass:"no-pad prel"
	},
	{
		key:"controls",
		label:"",
		thClass:"xs-col",
		tdClass:"no-pad prel"
	}
]